import React from "react";
import { Flame, Quote, MapPin, Layers, Infinity as InfinityIcon } from "lucide-react";
import SectionNumber from "./SectionNumber";

const STATS = [
  { n: "12+", l: "Évnyi tapasztalat", Icon: Flame },
  { n: "3", l: "Sütési technika", Icon: Layers },
  { n: "1", l: "Tisza-parti oázis", Icon: MapPin },
  { n: "∞", l: "Tüzes élmény", Icon: InfinityIcon },
];

export default function Intro() {
  return (
    <section
      id="intro"
      data-testid="intro-section"
      className="relative bg-white py-24 sm:py-32 overflow-hidden"
    >
      <SectionNumber n="01" />
      {/* Soft warm background blob */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #D90429 0%, #FFA64D 50%, transparent 70%)",
        }}
      />
      {/* Vertical decorative line with ember dot */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-pkash to-transparent -translate-x-1/2"
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center relative">
        {/* LEFT: Image stack */}
        <div className="lg:col-span-5 reveal">
          <div className="relative">
            {/* Primary tall image */}
            <div className="relative aspect-[4/5] overflow-hidden bg-pkbeige">
              <img
                src="/images/nkl5175x.jpg"
                alt="Kemence melege"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/30 via-transparent to-transparent" />

              {/* "Vezsenyen, 2014 óta" handwritten tag */}
              <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm px-3 py-2 shadow-md">
                <div className="text-[0.55rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                  Vezseny
                </div>
                <div className="font-serif italic text-pkcharcoal text-base leading-none mt-1">
                  2014 óta
                </div>
              </div>
            </div>

            {/* Smaller overlapping image */}
            <div className="hidden sm:block absolute -bottom-10 -right-6 w-2/5 aspect-[3/4] overflow-hidden border-[10px] border-white shadow-2xl bg-pkbeige rotate-[3deg] hover:rotate-0 transition-transform duration-700">
              <img
                src="/images/gsmcnsi3.jpg"
                alt="Egyedi tálalódeszka"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Vertical "smoke" red line + ember dot */}
            <div className="absolute -left-3 sm:-left-5 top-12 bottom-12 w-px bg-gradient-to-b from-pkred via-pkred/30 to-transparent">
              <span className="absolute -top-1 -left-[3px] w-[7px] h-[7px] rounded-full bg-pkred shadow-[0_0_12px_rgba(217,4,41,0.8)]" />
            </div>
          </div>
        </div>

        {/* RIGHT: Copy */}
        <div className="lg:col-span-7 reveal">
          <span className="eyebrow">Bemutatkozás</span>
          <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-[3.75rem] mt-6 leading-[1.02]">
            Élmény<span className="text-pkred">.</span>
            <br />
            <span className="italic font-light text-pkcharcoal/85">
              Nem csak étel.
            </span>
          </h2>

          {/* Pull-quote */}
          <div className="mt-10 relative pl-8 sm:pl-12 max-w-2xl">
            <Quote
              className="absolute -top-2 left-0 w-6 h-6 sm:w-8 sm:h-8 text-pkred/80"
              strokeWidth={1.2}
              fill="currentColor"
            />
            <p className="font-serif text-xl sm:text-2xl lg:text-[1.7rem] leading-[1.45] text-pkcharcoal font-light">
              A{" "}
              <span className="font-semibold text-pkred">Pakkaland</span>{" "}
              egy vidéki élménybirtok Vezsenyen, ahol a tűz nem csak főzési
              technika, hanem hangulat, közösség és történet.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="block w-8 h-px bg-pkred" />
              <span className="text-[0.65rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                A házigazdák
              </span>
            </div>
          </div>

          <p className="mt-10 text-pkcharcoal/70 text-base sm:text-lg leading-relaxed max-w-2xl">
            Itt az ételek lassan, parázs felett, kemencében vagy föld alatt
            készülnek, miközben a vendégek kiszakadnak a rohanásból. Egy hely,
            ahol az állatok közelsége, a fa füstje és a Tisza nyugalma
            találkozik.
          </p>

          {/* Stats grid */}
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-pkash">
            {STATS.map(({ n, l, Icon }, idx) => (
              <div
                key={l}
                data-testid={`intro-stat-${idx}`}
                className="group relative bg-white p-5 sm:p-6 hover:bg-pkbeige transition-colors duration-500 cursor-default"
              >
                <Icon
                  className="w-4 h-4 text-pkred/60 group-hover:text-pkred group-hover:scale-110 transition-all duration-500"
                  strokeWidth={1.6}
                />
                <div className="font-serif text-3xl sm:text-4xl text-pkcharcoal font-medium mt-3 leading-none group-hover:text-pkred transition-colors duration-500">
                  {n}
                </div>
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-pkcharcoal/55 mt-3">
                  {l}
                </div>
                {/* corner ember on hover */}
                <span className="absolute top-0 right-0 w-0 h-px bg-pkred group-hover:w-full transition-all duration-700" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
